import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TbAlertTriangle, TbUserCircle } from "react-icons/tb";
import { usePageList } from "../../pageConfig";
import { useAuth } from "../../AuthContext";
import { useChat } from "../../contexts/ChatContext";
import Services from "../../services/Services";
import ConsentLockNotice from "../../components/ConsentLockNotice/ConsentLockNotice";
import {
  MERCHANT_COMPANY_DOCUMENTS_PATH,
  merchantHasDepoType,
  merchantHasNakliyeType,
} from "../../utils/merchantNavigation";
// Müşteri anasayfasıyla AYNI görsel dil: hero carousel + "Hızlı Erişim" kartları.
// Stiller ortak (CustomerHome.css) — iki panelin anasayfası birebir aynı görünür.
import "./CustomerHome.css";

import hero1 from "../../assets/musteri-panel-hero-1.webp";
import hero3 from "../../assets/musteri-panel-hero-3.webp";
import hero4 from "../../assets/musteri-panel-hero-4.webp";

const HERO_SLIDES = [hero1, hero3, hero4];
const SLIDE_INTERVAL_MS = 5000;

// İş ortağı (merchant) anasayfası: müşteri anasayfasının merchant karşılığı.
// Kısayollar sidebar konfigürasyonundan (usePageList) türetilir; menü değişince burası da değişir.
export default function MerchantHome() {
  const navigate = useNavigate();
  const pageList = usePageList();
  const {
    merchantCompanyID,
    merchantCompanyTypes,
    documentsComplete,
    // Hangi başlığın eksik olduğunu yazabilmek için kırılım (documents/agreements/signature/bank).
    complianceBreakdown,
    agreementStatus,
    // Zorunlu onay kilidi (KVKK / Kullanıcı Sözleşmesi) merchant'ta da geçerlidir.
    customerMissingConsents = [],
    isCustomerConsentLocked,
    // Uyum bandı yalnız veri hazır olduğunda gösterilir (bkz. showComplianceWarning).
    status: authStatus,
  } = useAuth() || {};
  const chatUnread = useChat()?.totalUnread || 0;

  const [companyTitle, setCompanyTitle] = useState("");
  const [activeSlide, setActiveSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartXRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !merchantCompanyID) return undefined;
    let cancelled = false;
    Services.GetWarehouseCompany(token, merchantCompanyID)
      .then((res) => {
        if (cancelled || !res?.data?.status) return;
        const company = Array.isArray(res.data.result) ? res.data.result[0] : res.data.result;
        setCompanyTitle(String(company?.title || "").trim());
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [merchantCompanyID]);

  const goToSlide = useCallback((index) => {
    setActiveSlide((index + HERO_SLIDES.length) % HERO_SLIDES.length);
  }, []);

  // Otomatik ilerleme — yalnız aktif dokunuş sırasında durur (müşteri anasayfasıyla aynı kural).
  useEffect(() => {
    if (paused) return undefined;
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % HERO_SLIDES.length);
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [paused, activeSlide]);

  const handleTouchStart = (event) => {
    touchStartXRef.current = event.touches?.[0]?.clientX ?? null;
    setPaused(true);
  };

  const handleTouchEnd = (event) => {
    const startX = touchStartXRef.current;
    touchStartXRef.current = null;
    setPaused(false);
    if (startX === null) return;
    const endX = event.changedTouches?.[0]?.clientX ?? startX;
    const delta = endX - startX;
    if (Math.abs(delta) < 40) return;
    goToSlide(activeSlide + (delta < 0 ? 1 : -1));
  };

  // Sidebar'daki merchant sayfaları (Anasayfa hariç) + Profil kartı.
  const menuItems = useMemo(() => {
    const items = [];
    pageList.forEach((section) => {
      (section.items || []).forEach((item) => {
        if (!item?.path || item.path === "/homepage") return;
        items.push({ text: item.text, icon: item.icon, path: item.path });
      });
    });
    items.push({ text: "Profil", icon: <TbUserCircle strokeWidth={1} />, path: "/profile" });
    return items;
  }, [pageList]);

  // Hero CTA: firma tipine göre en çok kullanılan sayfa.
  const heroAction = useMemo(() => {
    if (merchantHasDepoType(merchantCompanyTypes)) return { label: "Depo Listem", path: "/warehouse-list" };
    if (merchantHasNakliyeType(merchantCompanyTypes)) return { label: "Nakliye Talepleri", path: "/transport-requests" };
    return { label: "Şirket Bilgilerim", path: "/my-warehouse-firm" };
  }, [merchantCompanyTypes]);

  // Kartlardaki kırmızı rozet: şimdilik okunmamış mesaj sayısı.
  const badges = useMemo(() => (chatUnread > 0 ? { "/chat": chatUnread } : {}), [chatUnread]);

  // Evrak/sözleşme uyarısı: onay tamamlanmadıysa firma ekranına yönlendiren bant gösterilir.
  const agreementApproved = String(agreementStatus ?? "") === "1";
  // Uyum verisi YÜKLENENE KADAR bant gösterilmez: userType, uyum isteklerinden ÖNCE set
  // edildiği için (documentsComplete başlangıçta false) tüm merchantlara uyarı basılıyordu.
  // Zorunlu onaylar verilmeden firma-eksikleri bandı GÖSTERİLMEZ: onay kilidindeyken bu
  // banttaki "Tamamla" butonları zaten disabled (hiçbir sayfaya gidilemiyor). Önce onaylar
  // alınır, kilit kalkınca bant kendiliğinden görünür.
  const showComplianceWarning =
    authStatus === "ready" && !isCustomerConsentLocked && (!documentsComplete || !agreementApproved);

  // Eksik başlıklar kalem kalem. Kırılım okunamadıysa (complianceBreakdown null) tek tek
  // işaretleme yapılmaz; yalnız sözleşme durumu bilinir. Her satır ilgili firma sekmesine gider.
  const missingComplianceItems = useMemo(() => {
    const items = [];
    const b = complianceBreakdown;
    if (b && !b.documents) {
      items.push({ label: "Zorunlu Evraklar", hint: "Eksik evraklarınızı yükleyin.", tab: "documents" });
    }
    if (!agreementApproved) {
      items.push({ label: "İş Birliği Sözleşmesi", hint: "Sözleşmeniz henüz onaylanmadı.", tab: "contracts" });
    }
    if (b && !b.signature) {
      items.push({ label: "İmza Yetkilisi, İmza ve Kaşe", hint: "İmza yetkilisi adı, firma imzası ve kaşesi gerekli.", tab: "profile" });
    }
    if (b && !b.bank) {
      items.push({ label: "Banka Bilgileri", hint: "En az bir banka hesabı eklemelisiniz.", tab: "bankInformations" });
    }
    return items;
  }, [complianceBreakdown, agreementApproved]);

  return (
    <div className="customer-home">
      <ConsentLockNotice consents={customerMissingConsents} />

      {showComplianceWarning && (
        <section className="consent-lock" aria-live="polite">
          <div className="consent-lock-head">
            <TbAlertTriangle size={22} />
            <div>
              <strong>Firma bilgilerinizde tamamlanması gerekenler var</strong>
              <p>
                {missingComplianceItems.length > 0
                  ? "Aşağıdaki bilgiler tamamlanmadan firmanıza teklif iletilemez."
                  : "Firma bilgilerinizde tamamlanması gereken kalemler var."}
              </p>
            </div>
          </div>
          <div className="consent-lock-items">
            {missingComplianceItems.length > 0 ? (
              missingComplianceItems.map((item) => (
                <div className="consent-lock-item" key={item.tab + item.label}>
                  <span className="consent-lock-title">
                    {item.label}
                    <small style={{ display: "block", fontWeight: 400, opacity: 0.75 }}>{item.hint}</small>
                  </span>
                  <button
                    type="button"
                    disabled={isCustomerConsentLocked}
                    onClick={() =>
                      !isCustomerConsentLocked &&
                      navigate("/my-warehouse-firm", { state: { initialTab: item.tab } })
                    }
                  >
                    Tamamla
                  </button>
                </div>
              ))
            ) : (
              // Kırılım okunamadıysa eski davranış: tek satır, şirket bilgilerine götürür.
              <div className="consent-lock-item">
                <span className="consent-lock-title">Evraklar ve Sözleşmeler</span>
                <button
                  type="button"
                  disabled={isCustomerConsentLocked}
                  onClick={() => !isCustomerConsentLocked && navigate(MERCHANT_COMPANY_DOCUMENTS_PATH)}
                >
                  Şirket Bilgilerim
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      <section
        className="customer-home-hero"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        aria-label="Tanıtım görselleri"
      >
        {HERO_SLIDES.map((src, index) => (
          <img
            key={src}
            src={src}
            alt=""
            className={`customer-home-hero-slide ${index === activeSlide ? "active" : ""}`}
            loading={index === 0 ? "eager" : "lazy"}
            draggable={false}
          />
        ))}
        <div className="customer-home-hero-overlay" />

        <div className="customer-home-hero-content">
          <span className="customer-home-hero-eyebrow">TROY DEPO PARTNER</span>
          <h1 className="customer-home-hero-title">
            {companyTitle ? `Hoş geldiniz, ${companyTitle}` : "Hoş geldiniz"}
          </h1>
          <p className="customer-home-hero-subtitle">
            Depo, nakliye ve müşteri operasyonlarınızı tek panelden yönetin.
          </p>
          <button
            type="button"
            className="customer-home-hero-cta"
            disabled={isCustomerConsentLocked}
            title={isCustomerConsentLocked ? "Önce güncel metinleri onaylamanız gerekiyor" : undefined}
            onClick={() => !isCustomerConsentLocked && navigate(heroAction.path)}
          >
            {heroAction.label}
          </button>
        </div>

        <div className="customer-home-hero-bars" role="tablist" aria-label="Görsel seçimi">
          {HERO_SLIDES.map((src, index) => (
            <button
              key={src}
              type="button"
              role="tab"
              aria-selected={index === activeSlide}
              aria-label={`Görsel ${index + 1}`}
              className={`customer-home-hero-bar ${index === activeSlide ? "active" : ""}`}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      </section>

      <section className="customer-home-menu" aria-label="Hızlı erişim">
        <span className="customer-home-menu-title">Hızlı Erişim</span>
        <div className="customer-home-menu-grid">
          {menuItems.map((item) => (
            <button
              key={item.path}
              type="button"
              className={`customer-home-menu-card${isCustomerConsentLocked ? " customer-home-menu-card--locked" : ""}`}
              disabled={isCustomerConsentLocked}
              title={isCustomerConsentLocked ? "Önce güncel metinleri onaylamanız gerekiyor" : undefined}
              onClick={() => !isCustomerConsentLocked && navigate(item.path)}
            >
              <span className="customer-home-menu-icon">{item.icon}</span>
              <span className="customer-home-menu-label">{item.text}</span>
              {badges[item.path] > 0 && (
                <span className="customer-home-menu-badge" title="Okunmamış mesaj">
                  {badges[item.path] > 99 ? "99+" : badges[item.path]}
                </span>
              )}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
